import type { AssetRef } from '@lp/shared';
import {
  el,
  type BlockSignal,
  type BlockSpec,
  type CtaValue,
  type IRContext,
  type IRNode,
  type Issue,
} from '@lp/shared';
import { isValidAnalyticsId, type ButtonRadius, type ButtonSize } from '../shared/ctaEmbed.js';
import { buttonNode, buttonStyleDefaults, buttonStyleFields, buttonStyleFrom } from '../shared/button.js';
import { compact, heading, image, paragraph, section, stack } from '../shared/helpers.js';
import { ctaGroupCls, stackCls } from '../shared/patterns.js';
import { boxCls } from '../shared/util.js';

export interface HeroSplitProps extends Record<string, unknown> {
  eyebrow: string;
  headline: string;
  subhead: string;
  cta: CtaValue;
  buttonSize: ButtonSize;
  buttonRadius: ButtonRadius;
  buttonFullWidth: boolean;
  image: AssetRef | null;
  trustLine: string;
  tone: 'white' | 'surface' | 'indigo';
  mediaSide: 'left' | 'right';
}

/** The SEO guide caps the H1 at 55 characters, keyword included. */
const H1_MAX = 55;

export const HeroSplit: BlockSpec<HeroSplitProps> = {
  id: 'HeroSplit',
  label: 'Hero',
  category: 'landing',
  description: 'Headline, supporting copy and a CTA beside an image. Use once, at the top.',

  fields: {
    eyebrow: { type: 'text', label: 'Eyebrow', help: 'Short label above the headline. Optional.', maxChars: 40, lint: 'body' },
    headline: { type: 'text', label: 'Headline (H1)', help: `Include your primary keyword. Aim for ${H1_MAX} characters or fewer.`, maxChars: H1_MAX, lint: 'heading' },
    subhead: { type: 'textarea', label: 'Supporting copy', maxChars: 240, lint: 'body' },
    cta: { type: 'cta', label: 'Call to action' },
    ...buttonStyleFields('button'),
    image: { type: 'image', label: 'Image', role: 'hero', help: 'Loads eagerly — this is your LCP element.' },
    trustLine: { type: 'text', label: 'Reassurance line', help: 'e.g. "No account needed for anyone paying in."', maxChars: 80, lint: 'body' },
    tone: {
      type: 'radio',
      label: 'Background',
      options: [
        { label: 'White', value: 'white' },
        { label: 'Soft grey', value: 'surface' },
        { label: 'Indigo', value: 'indigo' },
      ],
    },
    mediaSide: {
      type: 'radio',
      label: 'Image side',
      options: [
        { label: 'Right', value: 'right' },
        { label: 'Left', value: 'left' },
      ],
    },
  } as BlockSpec<HeroSplitProps>['fields'],

  defaultProps: {
    eyebrow: '',
    headline: 'Collect money from a group without the chasing',
    subhead:
      'Set up a pot, share the link, and watch the money land. Nobody paying in needs an account.',
    cta: {
      label: 'Collect money now',
      href: 'https://app.collctiv.com/signup',
      analyticsId: 'lpHeroCTA',
      location: 'website',
      utmContent: 'lp_hero',
    },
    ...buttonStyleDefaults('button'),
    image: null,
    trustLine: '',
    tone: 'surface',
    mediaSide: 'right',
  } as HeroSplitProps,

  toIR(props, ctx: IRContext): IRNode {
    const { tone } = props;

    const ctaGroup = el(
      'Block',
      'div',
      ctaGroupCls('left'),
      [buttonNode(props.cta, 'primary', buttonStyleFrom(props, 'button'))],
      { displayName: 'CTA group' },
    );

    const copy = stack(
      compact([
        paragraph('lp-small', props.eyebrow, tone),
        heading(1, props.headline, tone),
        paragraph('lp-lead', props.subhead, tone),
        ctaGroup,
        paragraph('lp-cta-note', props.trustLine, tone),
      ]),
      { displayName: 'Hero copy' },
    );

    const media = image(props.image ?? undefined, { isHero: ctx.isFirstSection });

    const splitCls = stackCls({
      row: true,
      // Flip visually, not structurally: the copy stays first in the markup so the H1 is
      // still the first thing a screen reader and a crawler meet.
      reverse: props.mediaSide === 'left',
      gap: '7',
      align: 'center',
    });

    const split = el(
      'Grid',
      'div',
      splitCls,
      compact([
        copy,
        media ? el('Block', 'div', boxCls('is-w-half'), [media], { displayName: 'Media' }) : null,
      ]),
      { displayName: 'Hero split' },
    );

    return section(tone, [split], { displayName: 'Hero' });
  },

  signals(props): BlockSignal[] {
    const out: BlockSignal[] = [
      { kind: 'heading', level: 1, text: props.headline },
      { kind: 'text', fieldPath: 'headline', value: props.headline, scope: 'heading' },
      { kind: 'text', fieldPath: 'subhead', value: props.subhead, scope: 'body' },
      { kind: 'cta', analyticsId: props.cta.analyticsId, tone: props.tone },
    ];
    if (props.eyebrow) out.push({ kind: 'text', fieldPath: 'eyebrow', value: props.eyebrow, scope: 'body' });
    if (props.trustLine) out.push({ kind: 'text', fieldPath: 'trustLine', value: props.trustLine, scope: 'body' });
    if (props.image) out.push({ kind: 'image', asset: props.image });
    return out;
  },

  validate(props): Issue[] {
    const issues: Issue[] = [];

    if (!props.headline.trim()) {
      issues.push({
        id: 'hero-no-h1',
        severity: 'error',
        message: 'The hero has no headline. Every page needs exactly one H1.',
        location: { fieldPath: 'headline' },
      });
    } else if (props.headline.length > H1_MAX) {
      issues.push({
        id: 'hero-h1-length',
        severity: 'warn',
        message: `Headline is ${props.headline.length} characters — aim for ${H1_MAX} or fewer.`,
        location: { fieldPath: 'headline' },
      });
    }

    if (!props.image) {
      issues.push({
        id: 'hero-no-image',
        severity: 'info',
        message: 'No hero image. The largest element above the fold is what Google times for LCP.',
        location: { fieldPath: 'image' },
      });
    }

    if (!isValidAnalyticsId(props.cta.analyticsId)) {
      issues.push({
        id: 'cta-analytics-id',
        severity: 'error',
        message:
          'The CTA needs a valid analytics id — it becomes the button’s DOM id and the ' +
          'position reported to PostHog. Letters, numbers, dashes and underscores only.',
        location: { fieldPath: 'cta' },
      });
    }

    return issues;
  },
};
